import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { GlassPanel, Icon } from '../components/common'
import { useProject } from '../context/ProjectContext'
import {
  FEATURE_TESTS,
  TEST_CATEGORIES,
  TEST_STATUS,
  getSortedCategories,
  getTestsByCategory,
  getTestResults,
  saveTestResult,
  resetAllTests,
  seedTestResults,
  getTestStats,
} from '../services/featureTests'
import './TestingView.css'

// ============================================
// Animation Variants
// ============================================

const pageVariants = {
  initial: { opacity: 0, y: 20, filter: 'blur(4px)' },
  animate: { 
    opacity: 1, 
    y: 0, 
    filter: 'blur(0px)',
    transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] }
  },
  exit: { opacity: 0, y: -20, filter: 'blur(4px)', transition: { duration: 0.2 } }
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
}

// ============================================
// Status Configuration
// ============================================

const STATUS_CONFIG = {
  passed: { iconName: 'check', label: 'Passed', color: 'var(--color-success)' },
  failed: { iconName: 'x', label: 'Failed', color: 'var(--color-error)' },
  in_progress: { iconName: 'sync', label: 'In Progress', color: 'var(--color-warning)' },
  untested: { iconName: 'node-circle', label: 'Untested', color: 'var(--text-muted)' },
}

// ============================================
// StatusBadge Component
// ============================================

function StatusBadge({ status, size = 'normal' }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.untested
  return (
    <span 
      className={`testing-view__status-badge testing-view__status-badge--${status} ${size === 'small' ? 'testing-view__status-badge--small' : ''}`}
    >
      <span>{config.icon}</span>
      <span>{config.label}</span>
    </span>
  )
}

// ============================================
// WaveformAnimation Component
// ============================================

function WaveformAnimation({ isActive, status = 'running' }) {
  const canvasRef = useRef(null)
  const animationRef = useRef(null)
  const timeRef = useRef(0)

  useEffect(() => {
    if (!canvasRef.current) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const width = canvas.width
    const height = canvas.height

    if (!isActive) {
      // Draw static state
      ctx.clearRect(0, 0, width, height)
      ctx.beginPath()
      ctx.strokeStyle = status === 'passed' 
        ? 'rgba(0, 214, 143, 0.5)' 
        : status === 'failed' 
          ? 'rgba(255, 61, 113, 0.5)' 
          : 'rgba(96, 96, 112, 0.3)'
      ctx.lineWidth = 2
      ctx.moveTo(0, height / 2)
      ctx.lineTo(width, height / 2)
      ctx.stroke()
      return
    }

    const animate = () => {
      timeRef.current += 0.08
      ctx.clearRect(0, 0, width, height)

      // Draw multiple waveforms
      const waves = [
        { color: 'rgba(0, 240, 255, 0.8)', amplitude: 12, frequency: 0.05, phase: 0 },
        { color: 'rgba(0, 240, 255, 0.5)', amplitude: 8, frequency: 0.08, phase: 1 },
        { color: 'rgba(0, 214, 143, 0.4)', amplitude: 6, frequency: 0.12, phase: 2 },
      ]

      waves.forEach((wave) => {
        ctx.beginPath()
        ctx.strokeStyle = wave.color
        ctx.lineWidth = 2

        for (let x = 0; x < width; x++) {
          const chirpFactor = 1 + (x / width) * 2
          const y = height / 2 + 
            wave.amplitude * Math.sin((x * wave.frequency * chirpFactor) + timeRef.current + wave.phase) *
            Math.exp(-((x - width * 0.7) ** 2) / (width * 80))
          
          if (x === 0) {
            ctx.moveTo(x, y)
          } else {
            ctx.lineTo(x, y)
          }
        }
        ctx.stroke()
      })

      ctx.shadowBlur = 10
      ctx.shadowColor = 'rgba(0, 240, 255, 0.5)'

      animationRef.current = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [isActive, status])

  return (
    <canvas
      ref={canvasRef}
      width={200}
      height={40}
      className="testing-view__waveform-canvas"
    />
  )
}

// ============================================
// ProgressRing Component
// ============================================

function ProgressRing({ progress, size = 32, strokeWidth = 2 }) {
  const radius = (size - strokeWidth) / 2
  const circumference = radius * 2 * Math.PI
  const offset = circumference - (progress / 100) * circumference

  return (
    <svg width={size} height={size} className="testing-view__progress-ring">
      <circle
        stroke="rgba(0, 240, 255, 0.2)"
        fill="transparent"
        strokeWidth={strokeWidth}
        r={radius}
        cx={size / 2}
        cy={size / 2}
      />
      <motion.circle
        stroke="url(#progressGradient)"
        fill="transparent"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        r={radius}
        cx={size / 2}
        cy={size / 2}
        style={{
          strokeDasharray: circumference,
          strokeDashoffset: offset,
          transform: 'rotate(-90deg)',
          transformOrigin: '50% 50%',
        }}
        initial={{ strokeDashoffset: circumference }}
        animate={{ strokeDashoffset: offset }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      />
      <defs>
        <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00f0ff" />
          <stop offset="100%" stopColor="#00d68f" />
        </linearGradient>
      </defs>
    </svg>
  )
}

// ============================================
// ProgressBar Component
// ============================================

function ProgressBar({ passed, failed, inProgress, untested, total }) {
  const passedPct = total > 0 ? (passed / total) * 100 : 0
  const failedPct = total > 0 ? (failed / total) * 100 : 0
  const inProgressPct = total > 0 ? (inProgress / total) * 100 : 0

  return (
    <div className="testing-view__progress-card">
      <div className="testing-view__progress-header">
        <Icon name="copy" size={16} />
        <span>Testing Progress</span>
      </div>
      <div className="testing-view__progress-bar">
        <motion.div
          className="testing-view__progress-segment testing-view__progress-segment--passed"
          initial={{ width: 0 }}
          animate={{ width: `${passedPct}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />
        <motion.div
          className="testing-view__progress-segment testing-view__progress-segment--failed"
          initial={{ width: 0 }}
          animate={{ width: `${failedPct}%` }}
          transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
        />
        <motion.div
          className="testing-view__progress-segment testing-view__progress-segment--in_progress"
          initial={{ width: 0 }}
          animate={{ width: `${inProgressPct}%` }}
          transition={{ duration: 0.5, ease: 'easeOut', delay: 0.2 }}
        />
      </div>
      <div className="testing-view__progress-legend">
        <span className="testing-view__legend-item">
          <span className="testing-view__legend-dot testing-view__legend-dot--passed" />
          Passed ({passed})
        </span>
        <span className="testing-view__legend-item">
          <span className="testing-view__legend-dot testing-view__legend-dot--failed" />
          Failed ({failed})
        </span>
        <span className="testing-view__legend-item">
          <span className="testing-view__legend-dot testing-view__legend-dot--in_progress" />
          In Progress ({inProgress})
        </span>
        <span className="testing-view__legend-item">
          <span className="testing-view__legend-dot testing-view__legend-dot--untested" />
          Untested ({untested})
        </span>
      </div>
    </div>
  )
}

// ============================================
// TestItem Component
// ============================================

function TestItem({ test, result, onUpdateStatus, projectId }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isRunning, setIsRunning] = useState(false)
  const [runProgress, setRunProgress] = useState(0)
  const [selectedStatus, setSelectedStatus] = useState(result?.status || TEST_STATUS.UNTESTED)
  const [notes, setNotes] = useState(result?.notes || '')
  const [currentLogs, setCurrentLogs] = useState([])
  const [hasBeenRun, setHasBeenRun] = useState(false)

  const logRef = useRef(null)

  // Auto-scroll log
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight
    }
  }, [currentLogs])

  const handleSave = async () => {
    setIsUpdating(true)
    try {
      await saveTestResult(projectId, {
        testId: test.id,
        status: selectedStatus,
        notes,
        testedAt: selectedStatus !== TEST_STATUS.UNTESTED ? new Date().toISOString() : null,
        testedBy: selectedStatus !== TEST_STATUS.UNTESTED ? 'user' : null,
      })
      onUpdateStatus()
    } catch (err) {
      console.error('Error updating test:', err)
    }
    setIsUpdating(false)
  }

  const handleRunTest = async () => {
    setIsRunning(true)
    setIsExpanded(true)
    setRunProgress(0)
    setCurrentLogs([])
    setHasBeenRun(true)

    const addLog = (message, type = 'info') => {
      setCurrentLogs(prev => [...prev, { message, type, timestamp: Date.now() }])
    }

    addLog('Initializing test environment...')

    // Simulate test execution
    const totalTime = 2500 + Math.random() * 1500
    const interval = 50
    const steps = totalTime / interval
    let currentStep = 0

    const progressInterval = setInterval(() => {
      currentStep++
      const linearProgress = currentStep / steps
      const easedProgress = 1 - Math.pow(1 - linearProgress, 3)
      setRunProgress(Math.min(easedProgress * 100, 95))

      // Add logs at progress points
      if (Math.abs(linearProgress - 0.2) < 0.02) addLog('▸ Loading test fixtures...')
      else if (Math.abs(linearProgress - 0.4) < 0.02) addLog('▸ Running validation checks...')
      else if (Math.abs(linearProgress - 0.6) < 0.02) addLog('▸ Executing test cases...')
      else if (Math.abs(linearProgress - 0.8) < 0.02) addLog('▸ Processing results...')
    }, interval)

    await new Promise(resolve => setTimeout(resolve, totalTime))
    
    clearInterval(progressInterval)
    setRunProgress(100)

    // Random pass/fail
    const newStatus = Math.random() > 0.2 ? TEST_STATUS.PASSED : TEST_STATUS.FAILED
    
    if (newStatus === TEST_STATUS.PASSED) {
      addLog('All checks passed', 'success')
    } else {
      addLog('Test failed - assertion error', 'error')
    }

    // Save result
    await saveTestResult(projectId, {
      testId: test.id,
      status: newStatus,
      notes: `Automated test run at ${new Date().toLocaleTimeString()}`,
      testedAt: new Date().toISOString(),
      testedBy: 'automated',
    })

    setSelectedStatus(newStatus)
    onUpdateStatus()
    setIsRunning(false)
  }

  const displayStatus = hasBeenRun ? selectedStatus : (result?.status || TEST_STATUS.UNTESTED)

  return (
    <motion.div
      className={`testing-view__test-item ${isRunning ? 'testing-view__test-item--running' : ''}`}
      layout
    >
      <div className="testing-view__test-header">
        <div 
          className="testing-view__test-info"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <span className="testing-view__expand-icon">
                {isExpanded ? <Icon name="chevron-down" size={14} /> : <Icon name="play" size={14} />}
          </span>
          <div className="testing-view__test-details">
            <span className="testing-view__test-name">{test.name}</span>
            <span className="testing-view__test-description">{test.description}</span>
          </div>
        </div>
        <div className="testing-view__test-actions">
          <StatusBadge status={result?.status || TEST_STATUS.UNTESTED} size="small" />
          <button
            className={`testing-view__run-btn ${isRunning ? 'testing-view__run-btn--running' : ''}`}
            onClick={handleRunTest}
            disabled={isRunning}
          >
            {isRunning ? (
              <>
                <span className="testing-view__zap-icon"><Icon name="events" size={14} /></span>
                Running...
              </>
            ) : (
              <>
                <span><Icon name="play" size={14} /></span>
                Run Test
              </>
            )}
          </button>
        </div>
      </div>

      {/* Waveform visualization */}
      {(hasBeenRun || isRunning) && (
        <div className="testing-view__visual-container">
          <div className="testing-view__visual-header">
            <div className={`testing-view__visual-title ${isRunning ? 'testing-view__visual-title--running' : displayStatus === 'passed' ? 'testing-view__visual-title--passed' : displayStatus === 'failed' ? 'testing-view__visual-title--failed' : ''}`}>
              <span className={isRunning ? 'testing-view__pulse-icon' : ''}>
                {isRunning ? <Icon name="sync" size={14} /> : <Icon name={STATUS_CONFIG[displayStatus]?.iconName || 'node-circle'} size={14} />}
              </span>
              <span>
                {isRunning ? 'Test Running' : displayStatus === 'passed' ? 'Test Passed' : displayStatus === 'failed' ? 'Test Failed' : 'Test Complete'}
              </span>
            </div>
            {isRunning && (
              <div className="testing-view__progress-info">
                <ProgressRing progress={runProgress} />
                <span className="testing-view__progress-pct">{Math.round(runProgress)}%</span>
              </div>
            )}
          </div>

          <div className="testing-view__visual-content">
            <div className="testing-view__waveform">
              <WaveformAnimation isActive={isRunning} status={displayStatus} />
              {!isRunning && displayStatus === 'passed' && (
                <motion.div
                  className="testing-view__success-overlay"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                >
                  <Icon name="check" size={24} />
                </motion.div>
              )}
              {!isRunning && displayStatus === 'failed' && (
                <motion.div
                  className="testing-view__failed-overlay"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                >
                  <Icon name="x" size={24} />
                </motion.div>
              )}
            </div>

            <div className="testing-view__terminal" ref={logRef}>
              <div className="testing-view__terminal-header">
                <span><Icon name="square" size={14} /></span>
                <span>output</span>
              </div>
              <div className="testing-view__terminal-content">
                {currentLogs.map((log, i) => (
                  <motion.div
                    key={i}
                    className={`testing-view__terminal-line ${log.type === 'error' ? 'testing-view__terminal-line--error' : ''} ${log.type === 'success' ? 'testing-view__terminal-line--success' : ''}`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {log.message}
                  </motion.div>
                ))}
                {isRunning && (
                  <motion.span
                    className="testing-view__cursor"
                    animate={{ opacity: [1, 0, 1] }}
                    transition={{ repeat: Infinity, duration: 1 }}
                  >
                    █
                  </motion.span>
                )}
              </div>
            </div>
          </div>

          {isRunning && (
            <div className="testing-view__status-bar">
              <div className="testing-view__status-bar-fill" style={{ width: `${runProgress}%` }} />
              <div className="testing-view__status-bar-glow" style={{ left: `${runProgress}%` }} />
            </div>
          )}
        </div>
      )}

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            className="testing-view__test-expanded"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            {test.steps && test.steps.length > 0 && (
              <div className="testing-view__test-steps">
                <h4>Test Steps</h4>
                <ol>
                  {test.steps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </div>
            )}

            <div className="testing-view__status-select">
              <label>Update Status:</label>
              <div className="testing-view__status-options">
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <button
                    key={key}
                    className={`testing-view__status-option ${selectedStatus === key ? 'testing-view__status-option--selected' : ''}`}
                    onClick={() => setSelectedStatus(key)}
                    style={{ '--status-color': config.color }}
                  >
                    <span>{config.icon}</span>
                    {config.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="testing-view__notes-section">
              <label>Notes:</label>
              <textarea
                className="testing-view__notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add testing notes..."
                rows={2}
              />
            </div>

            {result?.testedAt && (
              <div className="testing-view__tested-info">
                Last tested: {new Date(result.testedAt).toLocaleDateString()} by {result.testedBy || 'unknown'}
              </div>
            )}

            <div className="testing-view__test-footer">
              <button
                className="testing-view__test-footer-btn"
                onClick={() => setIsExpanded(false)}
              >
                <Icon name="close" size={14} /> Close
              </button>
              <button
                className="testing-view__test-footer-btn testing-view__test-footer-btn--primary"
                onClick={handleSave}
                disabled={isUpdating}
              >
                {isUpdating ? 'Saving...' : <><Icon name="save" size={14} /> Save</>}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

// ============================================
// TestSection Component
// ============================================

function TestSection({ category, tests, results, onUpdateStatus, projectId }) {
  const [isCollapsed, setIsCollapsed] = useState(false)

  const stats = useMemo(() => {
    const passed = tests.filter(t => results[t.id]?.status === TEST_STATUS.PASSED).length
    const failed = tests.filter(t => results[t.id]?.status === TEST_STATUS.FAILED).length
    const inProgress = tests.filter(t => results[t.id]?.status === TEST_STATUS.IN_PROGRESS).length
    return { passed, failed, inProgress, total: tests.length }
  }, [tests, results])

  const completionPct = stats.total > 0 ? Math.round((stats.passed / stats.total) * 100) : 0

  return (
    <motion.div 
      className={`testing-view__section ${!isCollapsed ? 'testing-view__section--expanded' : ''}`}
      variants={itemVariants}
    >
      <div
        className="testing-view__section-header"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="testing-view__section-header-left">
                <span className="testing-view__section-toggle"><Icon name="play" size={12} /></span>
          <div className="testing-view__section-icon"><Icon name={category.iconName} size={18} /></div>
          <div className="testing-view__section-title-block">
            <h3 className="testing-view__section-title">{category.name}</h3>
            <span className="testing-view__section-subtitle">
              {stats.passed}/{stats.total} tests passed
            </span>
          </div>
        </div>
        <div className="testing-view__section-stats">
          <div className="testing-view__section-progress">
            <div
              className="testing-view__section-progress-fill"
              style={{ width: `${completionPct}%` }}
            />
          </div>
          <span className="testing-view__section-pct">{completionPct}%</span>
        </div>
      </div>

      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            className="testing-view__section-content"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="testing-view__section-meta">
              <div className="testing-view__section-description">
                <Icon name="info" size={16} />
                <p>{category.description}</p>
              </div>
            </div>

            <div className="testing-view__test-list">
              {tests.map((test) => (
                <TestItem
                  key={test.id}
                  test={test}
                  result={results[test.id]}
                  onUpdateStatus={onUpdateStatus}
                  projectId={projectId}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

// ============================================
// Main TestingView Component
// ============================================

export default function TestingView() {
  const { projectId } = useProject()
  const [results, setResults] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const [isSeeding, setIsSeeding] = useState(false)
  const [isResetting, setIsResetting] = useState(false)
  const [actionMessage, setActionMessage] = useState(null)

  // Load test results
  const loadResults = useCallback(async () => {
    try {
      const data = await getTestResults(projectId)
      setResults(data)
      setError(null)
    } catch (err) {
      console.error('Error loading test results:', err)
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    loadResults()
  }, [loadResults])

  // Group tests by category
  const categories = useMemo(() => getSortedCategories(), [])

  const testsByCategory = useMemo(() => {
    const grouped = {}
    categories.forEach(cat => {
      grouped[cat.id] = getTestsByCategory(cat.id)
    })
    return grouped
  }, [categories])

  // Filter tests
  const filteredTestsByCategory = useMemo(() => {
    if (statusFilter === 'all') return testsByCategory

    const filtered = {}
    Object.entries(testsByCategory).forEach(([catId, tests]) => {
      filtered[catId] = tests.filter(t => {
        const status = results[t.id]?.status || TEST_STATUS.UNTESTED
        return status === statusFilter
      })
    })
    return filtered
  }, [testsByCategory, results, statusFilter])

  // Calculate stats
  const stats = useMemo(() => {
    const passed = FEATURE_TESTS.filter(t => results[t.id]?.status === TEST_STATUS.PASSED).length
    const failed = FEATURE_TESTS.filter(t => results[t.id]?.status === TEST_STATUS.FAILED).length
    const inProgress = FEATURE_TESTS.filter(t => results[t.id]?.status === TEST_STATUS.IN_PROGRESS).length
    const untested = FEATURE_TESTS.length - passed - failed - inProgress
    return { passed, failed, inProgress, untested, total: FEATURE_TESTS.length }
  }, [results])

  const overallProgress = stats.total > 0 ? Math.round((stats.passed / stats.total) * 100) : 0

  // Action handlers
  const handleSeedTests = async () => {
    setIsSeeding(true)
    setActionMessage(null)
    try {
      await seedTestResults(projectId)
      await loadResults()
      setActionMessage({ type: 'success', text: `Seeded ${FEATURE_TESTS.length} tests` })
    } catch (err) {
      console.error('Error seeding tests:', err)
      setActionMessage({ type: 'error', text: `Failed to seed tests: ${err.message}` })
    }
    setIsSeeding(false)
    setTimeout(() => setActionMessage(null), 5000)
  }

  const handleResetAll = async () => {
    if (!window.confirm('Are you sure you want to reset all tests to "untested"? This cannot be undone.')) {
      return
    }
    setIsResetting(true)
    setActionMessage(null)
    try {
      await resetAllTests(projectId)
      await loadResults()
      setActionMessage({ type: 'success', text: 'All tests reset to untested' })
    } catch (err) {
      console.error('Error resetting tests:', err)
      setActionMessage({ type: 'error', text: `Failed to reset tests: ${err.message}` })
    }
    setIsResetting(false)
    setTimeout(() => setActionMessage(null), 5000)
  }

  if (loading) {
    return (
      <div className="testing-view__loading">
        <div className="testing-view__spinner" />
        <p>Loading feature tests...</p>
      </div>
    )
  }

  if (error) {
    return (
      <GlassPanel glow="amber" padding="lg" className="testing-view__error">
        <h3>Error loading tests</h3>
        <p>{error.message}</p>
      </GlassPanel>
    )
  }

  return (
    <motion.div
      className="testing-view"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Header */}
      <header className="testing-view__header">
        <div className="testing-view__title-group">
          <div className="testing-view__icon"><Icon name="testing" size={28} /></div>
          <h1 className="testing-view__title">Feature Testing</h1>
        </div>
        <p className="testing-view__subtitle">
          Verify all system components are working correctly
        </p>
      </header>

      {/* Action Buttons */}
      <div className="testing-view__actions">
        <button
          className="testing-view__action-btn testing-view__action-btn--seed"
          onClick={handleSeedTests}
          disabled={isSeeding || isResetting}
        >
          {isSeeding ? (
            <>
              <span className="testing-view__spin-icon">⟳</span>
              Seeding...
            </>
          ) : (
            <>
              <Icon name="database" size={14} />
              Seed Tests
            </>
          )}
        </button>
        <button
          className="testing-view__action-btn testing-view__action-btn--reset"
          onClick={handleResetAll}
          disabled={isSeeding || isResetting}
        >
          {isResetting ? (
            <>
              <span className="testing-view__spin-icon">⟳</span>
              Resetting...
            </>
          ) : (
            <>
              <span>↺</span>
              Reset All
            </>
          )}
        </button>
        {actionMessage && (
          <motion.div
            className={`testing-view__action-message testing-view__action-message--${actionMessage.type}`}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            {actionMessage.type === 'success' ? <Icon name="check" size={14} /> : <Icon name="x" size={14} />} {actionMessage.text}
          </motion.div>
        )}
      </div>

      {/* Stats Grid */}
      <motion.div 
        className="testing-view__stats"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="testing-view__stat-card testing-view__stat-card--primary" variants={itemVariants}>
                <div className="testing-view__stat-icon"><Icon name="stats" size={20} /></div>
          <div className="testing-view__stat-info">
            <span className="testing-view__stat-label">Overall Progress</span>
            <span className="testing-view__stat-value">{overallProgress}%</span>
          </div>
        </motion.div>
        <motion.div className="testing-view__stat-card testing-view__stat-card--success" variants={itemVariants}>
          <div className="testing-view__stat-icon"><Icon name="check" size={20} /></div>
          <div className="testing-view__stat-info">
            <span className="testing-view__stat-label">Passed</span>
            <span className="testing-view__stat-value">{stats.passed}</span>
          </div>
        </motion.div>
        <motion.div className="testing-view__stat-card testing-view__stat-card--danger" variants={itemVariants}>
          <div className="testing-view__stat-icon"><Icon name="x" size={20} /></div>
          <div className="testing-view__stat-info">
            <span className="testing-view__stat-label">Failed</span>
            <span className="testing-view__stat-value">{stats.failed}</span>
          </div>
        </motion.div>
        <motion.div className="testing-view__stat-card testing-view__stat-card--warning" variants={itemVariants}>
          <div className="testing-view__stat-icon">◷</div>
          <div className="testing-view__stat-info">
            <span className="testing-view__stat-label">In Progress</span>
            <span className="testing-view__stat-value">{stats.inProgress}</span>
          </div>
        </motion.div>
        <motion.div className="testing-view__stat-card testing-view__stat-card--muted" variants={itemVariants}>
          <div className="testing-view__stat-icon"><Icon name="node-circle" size={20} /></div>
          <div className="testing-view__stat-info">
            <span className="testing-view__stat-label">Untested</span>
            <span className="testing-view__stat-value">{stats.untested}</span>
          </div>
        </motion.div>
      </motion.div>

      {/* Progress Bar */}
      <ProgressBar {...stats} />

      {/* Filter Bar */}
      <div className="testing-view__filters">
        <span className="testing-view__filter-label">
          <Icon name="search" size={14} />
          <span>Filter:</span>
        </span>
        <div className="testing-view__filter-buttons">
          {['all', 'passed', 'failed', 'in_progress', 'untested'].map((filter) => (
            <button
              key={filter}
              className={`testing-view__filter-btn ${statusFilter === filter ? 'testing-view__filter-btn--active' : ''}`}
              onClick={() => setStatusFilter(filter)}
            >
              {filter === 'all' ? 'All' : STATUS_CONFIG[filter]?.label || filter}
            </button>
          ))}
        </div>
      </div>

      {/* Test Sections */}
      <motion.div 
        className="testing-view__sections"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {categories.map((category) => {
          const tests = filteredTestsByCategory[category.id] || []
          if (tests.length === 0 && statusFilter !== 'all') return null

          return (
            <TestSection
              key={category.id}
              category={category}
              tests={testsByCategory[category.id] || []}
              results={results}
              onUpdateStatus={loadResults}
              projectId={projectId}
            />
          )
        })}
      </motion.div>

      {/* Empty State */}
      {FEATURE_TESTS.length === 0 && (
        <div className="testing-view__empty">
          <Icon name="testing" size={48} />
          <h3>No Tests Defined</h3>
          <p>No feature tests have been defined yet.</p>
        </div>
      )}
    </motion.div>
  )
}
